<template>
  <lazy-component class="timeline-lazy-wrapper" ref="lazy">
    <div class="timeline-pane" ref="container" :class="{overlay: isOverlay}">
      <div class="timeline-title">timeline</div>
      <div class="gradient-cover"></div>
      <div ref="timelineEmbed" id="timeline-embed" class="timeline-embed ">
      </div>
    </div>
  </lazy-component>
</template>

<script>
  import get from 'lodash/get'
  import forEach from 'lodash/forEach'
  import throttle from 'lodash/throttle'
  import { isDesktop } from 'mixins/utilities'
  import isEmpty from 'lodash/isEmpty'

  export default {
    name: 'timeline',
    props: {
      nid: {
        type: [String, Number],
        required: true,
      },
      isOverlay: {
        type: Boolean,
        default: false,
      },
      timelineConfig: {
        type: Object,
        default: () => ({
          scale_factor: 1,
          height: 600,
          marker_padding: 5,
        }),
      },
    },
    data() {
      return {
        tlLoaded: false,
        resizeHandler: false,
      }
    },
    methods: {
      timelineReady(selector, time = 1000, interval = 500) {
        let timer = 0
        const domIsReady = setInterval(() => {
          timer += interval
          if (timer < time && !this.tlLoaded) {
            this.tlLoaded = !isEmpty(document.querySelectorAll(selector))
          } else {
            clearInterval(domIsReady)
          }
        }, interval)
      },
      timelineResize(resize = false) {
        const trianglePadding = 17
        const timenav = document.querySelector('.tl-timenav')
        const timenavAxis = document.querySelector('.tl-timeaxis-background')
        const timelineHeight = timenav.offsetHeight - timenavAxis.offsetHeight

        const timelineTimemarkers = document.querySelectorAll('.tl-timemarker')
        forEach(timelineTimemarkers, (timelineTimemarker) => {
          const resizeMarkerLine = (el) => {
            el.style.height = `${timelineHeight + trianglePadding}px`
            el.style.transform = `translateY(-${parseInt(timelineTimemarker.style.top, 10) + trianglePadding}px)`
          }
          if (!resize) {
            const lineElement = document.createElement('div')
            lineElement.classList.add('timeline-marker-line')
            resizeMarkerLine(lineElement)
            timelineTimemarker.appendChild(lineElement)
          } else {
            const lineElement = timelineTimemarker.querySelector('.timeline-marker-line')
            if (lineElement) {
              resizeMarkerLine(lineElement)
            }
          }
        })
      },
      updateDisplayHandler() {
        if (!isEmpty(window.timeline)) {
          setTimeout(() => {
            window.timeline.updateDisplay()
          }, 50)
        }
      },
      initTimeline() {
        // do stuff with the script
        window.timeline = new window.TL.Timeline(this.$refs.timelineEmbed, `/api/timeline/feed/${this.nid}`, this.timelineConfig)
        // A tricky way to disable the weird swipe by timeline js......
        window.TL.Browser.touch = false
        this.timelineReady('.tl-timemarker-timespan', 3000)

        let swiping = true
        const hammer = require('hammerjs')
        const timelineHammerManager = new hammer.Manager(this.$refs.container, {
          touchAction: 'pan-y',
          recognizers: [
            [hammer.Pan, { direction: hammer.DIRECTION_ALL, threshold: 10 }],
            [hammer.Tap],
          ],
        })

        window.addEventListener('orientationchange', this.updateDisplayHandler)

        // The lib have some issue when calling goToNext()without checking, but the checking method _getSlideIndex() cannot be called directly, we thus simulating its keydown event to trigger the next() and prev()
        timelineHammerManager.on('panstart', (_e) => {
          swiping = false
        })

        timelineHammerManager.on('panleft', (_e) => {
          if (!swiping && !isDesktop()) {
            swiping = true
            const arrowRightKeydown = new window.Event('keydown')
            arrowRightKeydown.key = 'ArrowRight'
            document.dispatchEvent(arrowRightKeydown)
          }
        })
        timelineHammerManager.on('panright', (_e) => {
          if (!swiping && !isDesktop()) {
            swiping = true
            const arrowLeftKeydown = new window.Event('keydown')
            arrowLeftKeydown.key = 'ArrowLeft'
            document.dispatchEvent(arrowLeftKeydown)
          }
        })

        this.resizeHandler = throttle((_e) => {
          setTimeout(() => {
            this.timelineResize(true)
          }, 50)
        }, 100)
        window.addEventListener('resize', this.resizeHandler)
      },
    },
    mounted() {
      this.$watch('$refs.lazy.state.loaded', () => {
        if (this.$refs.lazy.state.loaded) {
          if (!get(window, 'TL')) {
            require('./../../../assets/js/lib/timeline/timeline')
          }
          this.initTimeline()
        }
      })
    },
    destroyed() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
      }
      window.removeEventListener('orientationchange', this.updateDisplayHandler)
    },
    watch: {
      tlLoaded(val) {
        if (val) {
          this.timelineResize()
        }
      },
      nid() {
        this.initTimeline()
      },
    },
  }
</script>


<style lang="scss">
  @import './../../../assets/css/timeline/timeline.css';
  $timemarker-width: 150;
  .timeline-title {
    position: absolute;
    font-size: rem-calc(22);
    line-height: rem-calc(30);
    width: 100%;
    color: $rose;
    font-weight: bold;
    text-transform: uppercase;
    max-width: rem-calc(700);
    left: 50%;
    transform: translateX(-50%);
    top: rem-calc(10);
    padding-left: rem-calc(20);
    z-index: 5;
    @include respond-to('desktop') {
      top: rem-calc(15);
    }
    .related-person-overlay & {
      @include respond-to('desktop') {
        top: rem-calc(10);
      }
    }
  }

  .overlay .timeline-lazy-wrapper {
    @include respond-to('desktop') {
      margin: 0 rem-calc(-8px);
    }
  }

  .timeline-pane {
    position: relative;
    min-height: rem-calc(530);
    width: 100%;
  }

  .gradient-cover {
    width: 100%;
    height: 100%;
    position: absolute;
    border-top: 1px solid $dark-cerulean-border;
    top: 0;
    left: 0;
    background-image: linear-gradient($dark-cerulean 0%, $prussian-blue-2 55%);
  }

  .tl-timeline {
    background-color: transparent;
    position: absolute;
    height: rem-calc(600);

    @include respond-to('mobile') {
      height: rem-calc(530);
    }

    .overlay & {
      height: rem-calc(530);
    }
  }

  .tl-caption {
    display: none;
  }

  .tl-text-content-container {
    max-width: rem-calc(600);
    margin: 0 auto;
    color: $white;
    h3.tl-headline-date,
    .tl-headline-date {
      font-size: rem-calc(16);
      line-height: rem-calc(22);
      color: currentColor;
    }

    h2.tl-headline,
    .tl-headline {
      @include respond-to('desktop') {
        width: rem-calc(500);
      }
      text-transform: uppercase;
      color: currentColor;
      font-size: rem-calc(30);
      line-height: rem-calc(30);
      font-weight: 600;
      overflow: hidden;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
    }

    .tl-text-content {
      width: 95%;
      p {
        color: currentColor;
        font-size: rem-calc(16);
        line-height: rem-calc(24);
        font-family: $roboto;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .tl-timenav {
    overflow: visible;
    background-color: $timeline-bg;
    bottom: 0;
    position: absolute;
    border: none;
    z-index: 4;

    &:after {
      content: ' ';
      background-image: url('/static/svg/timeline_triangle.svg');
      background-size: contain;
      width: rem-calc(35);
      height: rem-calc(17);
      left: 50%;
      top: rem-calc(-18);
      position: absolute;
      transform: translateX(-50%);
      z-index: 10;
    }
  }

  .tl-timeaxis-background {
    background: $prussian-blue-2;
    border: none;
  }

  .tl-layout-portrait .tl-storyslider {
    box-shadow: none;
  }

  .tl-storyslider {
    background-color: transparent;
    z-index: 3;
    &:before {
      content: ' ';
      position: absolute;
      bottom: 0;
      width: 100%;
      left: 0;
      background-color: #0E2039;
      height: rem-calc(17);
      border-top: rem-calc(1) solid #304055;
    }
    .tl-slider-background {
      visibility: hidden;
    }
  }

  .tl-media-content-container {
    text-align: left;
  }

  .tl-timemarker.tl-timemarker-active .tl-timemarker-timespan {
    visibility: hidden;
  }

  .tl-timemarker-media-container {
    display: none;
  }

  .tl-timemarker {
    &.tl-timemarker-active {
      .tl-timemarker-content {
        background-color: $white;
        &:after {
          border-color: $white transparent transparent transparent;
        }
      }

      .tl-timemarker-line-left {
        display: none;
        border-color: $rose;
        border-left-width: rem-calc(2);
        box-shadow: none;
        margin-top: rem-calc(0);
        &:after {
          background-color: $rose;
        }
      }

      .tl-headline {
        color: $black;
      }

    }

    .tl-timemarker-line-left {
      box-shadow: none;
      display: none;
      margin-top: rem-calc(0);
    }

    .tl-timemarker-content-container {
      border-radius: 0;
      background-color: transparent;
      border: none;
      box-shadow: none;

      width: rem-calc($timemarker-width);

      .tl-timemarker-content {
        height: rem-calc(30);
      }
    }

    .tl-timemarker-content {
      padding: rem-calc(8 6 8 4);
      background-color: $black;
      &:after {
        content: ' ';
        width: 0;
        height: 0;
        border-style: solid;
        border-width: rem-calc(7 7 0 0);
        border-color: $black transparent transparent transparent;
        bottom: 0;
        left: 0;
        position: absolute;
        transform: translateY(100%);
      }
    }

    // Firefow fix: since timeline.js adds height
    .tl-timemarker-text .tl-headline {
      // Truncate
      width: rem-calc($timemarker-width - 10); // less total left/right padding
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .tl-headline {
      color: $white-50;
    }
  }

  .tl-slidenav-title {
    overflow: hidden;
    max-height: rem-calc(65);
  }

  .tl-slidenav-next,
  .tl-slidenav-previous {
    top: 65% !important;
    .tl-slidenav-icon {
      margin-bottom: 0;
      margin-right: 0;
      margin-left: 0;
      display: inline-block;
      width: rem-calc(18);
      height: rem-calc(30);
      &:before {
        width: 100%;
        height: 100%;
        display: inline-block;
        content: ' ';
        background-size: 100%;
      }
    }
    .tl-slidenav-title {
      font-size: rem-calc(14);
      line-height: rem-calc(16);
      color: $white;
      display: -webkit-box;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-top: rem-calc(-10);
    }
    .tl-slidenav-content-container {
      z-index: 21;
    }
    .tl-slidenav-description {
      opacity: .4;
      color: $white;
      font-size: rem-calc(12);
      line-height: rem-calc(18);
    }
  }

  .tl-slidenav-next {
    z-index: 21;

    .tl-slidenav-content-container {
      .tl-slidenav-icon {
        @include respond-to('mobile') {
          margin-right: 0;
        }
      }
    }

    .tl-slidenav-icon {
      &:before {
        right: 0;
        background-image: url('/static/svg/icon_arrow_right_8bit_white.svg');
      }
    }
  }

  .tl-slidenav-previous {
    z-index: 21;
    .tl-slidenav-icon {
      &:before {
        left: 0;
        background-image: url('/static/svg/icon_arrow_left_8bit_white.svg');
      }
    }
  }

  .tl-menubar {
    display: none;
  }

  .tl-timemarker-active {
    .timeline-marker-line {
      background-color: $rose;
      &:after {
        background-color: $rose;
      }
    }
  }

  .timeline-marker-line {
    width: rem-calc(1);
    background-color: $light-gray;
    position: relative;
    z-index: 4;
    &:after {
      content: ' ';
      position: absolute;
      bottom: rem-calc(-3);
      left: rem-calc(-3);
      width: rem-calc(6);
      height: rem-calc(6);
      border-radius: 50%;
      display: inline-block;
      background-color: $light-gray;

    }
  }

  .tl-slide {
    box-sizing: border-box;
    padding-top: rem-calc(50);
    height: 90%;
    overflow-y: hidden;

    &-scrollable-container {
      overflow-y: hidden;
    }

    .tl-slide-content-container {
      display: flex;
      width: 100%;
      align-items: flex-start;
      .tl-slide-content {
        display: table-row;
        .tl-media {
          min-width: 0;
          width: rem-calc(380);
          height: rem-calc(220);
          margin-right: rem-calc(20);
          display: table-cell
        }
        .tl-text {
          max-width: 100%;
          width: auto;
          padding-left: 0;
          padding-right: 0;
        }
      }
    }
    .tl-slide-content {
      display: flex;
      box-sizing: border-box;
      width: 100% !important;
      padding-left: rem-calc(220) !important;
      padding-right: rem-calc(100) !important;
    }

    &:not(.tl-slide-text-only) {
      .tl-slide-content-container {
        padding-top: rem-calc(29);
        .tl-text {
          padding-top: rem-calc(20);
        }
      }
    }

    &.tl-slide-text-only {
      .tl-slide-content-container {
        .tl-text {
          padding-top: rem-calc(70);
        }
      }
    }
  }

  .tl-timenav {
    height: rem-calc(242) !important;
  }

  .tl-storyslider {
    height: rem-calc(358px) !important;
  }

  // Mobile theme
  .tl-skinny {
    .tl-slide .tl-slide-content-container {
      padding-top: 0;
    }

    .tl-timenav {
      height: rem-calc(174) !important;
    }

    .tl-storyslider {
      height: rem-calc(356) !important;
    }

    .tl-media-content {
      @include respond-to('mobile') {
        width: 100%;
      }
      width: 94%;
      text-align: left;
    }

    .tl-text-content-container {
      h3.tl-headline-date,
      .tl-headline-date {
        font-size: rem-calc(14);
        line-height: rem-calc(14);
        font-weight: 600;
      }

      h2.tl-headline,
      .tl-headline {
        font-size: rem-calc(20);
        line-height: rem-calc(24);
      }
    }

    .tl-slide {
      padding-top: 0;
      height: 80%;
      margin-top: rem-calc(28);
      overflow-y: auto;
      height: 300px;

      &-scrollable-container {
        overflow-y: auto;
        width: 100%;
      }

      .tl-slide-content-container {
        align-items: flex-start;
        padding-top: 0;
        // Can only use important to override inline style injected by js
        .tl-slide-content {
          @include respond-to('desktop') {
            width: 90% !important;
          }
          display: flex;
          width: 100% !important;
          padding-left: rem-calc(25) !important;
          padding-right: rem-calc(25) !important;
          .tl-media {
            max-width: 100% !important;
            padding-top: 0;
          }
          .tl-text {
            padding-left: 0;
            padding-top: 0;
            margin-top: rem-calc(17);
            .tl-text-content-container {
              padding-left: 0;
              padding-right: 0;
              padding-bottom: rem-calc(12);
              p {
                margin-bottom: 0;
              }

              h2.tl-headline,
              .tl-headline {
                height: auto;
              }
            }
          }
        }
      }
    }

    .tl-slidenav-next,
    .tl-slidenav-previous {
      @include respond-to('mobile') {
        display: none;
      }
      .tl-slidenav-icon {
        width: rem-calc(12);
        height: rem-calc(20);
      }
    }
    .tl-media {
      height: rem-calc(166);
    }
    .tl-media-item {
      max-height: rem-calc(166) !important; // override library inline styling
      height: rem-calc(166) !important;
      width: auto;
    }
  }

  // Desktop theme
  .tl-layout-landscape {
    .tl-slide {
      padding-left: rem-calc(10 + 5); // account for .tl-slidenav-previous margin + allowance
      padding-right: rem-calc(10); // account for .tl-slidenav-next margin
    }
  }

  // This is brand of the JS library,
  // We must not let it appear so I use important here
  .tl-attribution {
    display: none !important;
  }

</style>



// WEBPACK FOOTER //
// Timeline.vue?22152bfc